// Single source of truth for "may this employee see / do visitor things" — RON-5466.
//
// Combines the two independent gates so no screen has to know about either storage detail:
//   1. Company entitlement  → tblcompany_property (visitor_portal / contractor_portal / max_sites)
//   2. Host invite rights   → tblvisitor_host_permission (RON-5463)
//
// Used by the Sidebar (to show/hide the Visitors item) and by VisitorUI (to decide whether the
// route renders the module or the unavailable state).
//
// COVERAGE CAVEAT FOR WIRING TIME (flagged, not yet a problem while mocked):
// the agreed carrier for the entitlement flags is `HomeScreenBookings_api`, which today is
// only called by the Home screen (`/booking`). A user who lands directly on `/visitors`, or a
// Teams user who lands on `/calendar`, would therefore have no flags yet and — because the
// default is OFF — would not see the module. `HasEmergencyRoles` avoids this by defaulting to
// visible AND being synced from `Calendar_api` / `MapGrid_api` / `MapCanvas_api` too. When the
// flags are wired, they should ride on a payload every entry path receives (`Profile_api` is
// the natural candidate) or be added to the same broad endpoint set.

import { useCallback, useEffect, useState } from "react";

import {
  VISITOR_ENTITLEMENT_EVENT,
  getVisitorEntitlement,
} from "../../utils/visitorEntitlement.js";
import {
  MOCK_ENTITLEMENT,
  MOCK_HOST_PERMISSIONS,
  USE_VISITOR_MOCKS,
} from "./visitorMockData.js";

const NO_PERMISSIONS = {
  canVisitor: false,
  canContractor: false,
  canParking: false,
  canDesk: false,
  canReception: false,
};

const readEntitlement = () =>
  USE_VISITOR_MOCKS ? MOCK_ENTITLEMENT : getVisitorEntitlement();

// Host rights have no real source yet (RON-5463 is a separate task and the flags are not on
// any response this app already reads). Until then an unmocked build grants nothing, so the
// module stays invisible rather than showing actions the backend would reject.
const readPermissions = () =>
  USE_VISITOR_MOCKS ? MOCK_HOST_PERMISSIONS : NO_PERMISSIONS;

export function useVisitorAccess() {
  const [entitlement, setEntitlement] = useState(readEntitlement);
  const [permissions, setPermissions] = useState(readPermissions);

  const refresh = useCallback(() => {
    setEntitlement(readEntitlement());
    setPermissions(readPermissions());
  }, []);

  useEffect(() => {
    // Same event-bus approach the Sidebar already uses for `hasEmergencyRolesChanged`, so a
    // mid-session entitlement change reaches every mounted consumer.
    window.addEventListener(VISITOR_ENTITLEMENT_EVENT, refresh);

    return () => {
      window.removeEventListener(VISITOR_ENTITLEMENT_EVENT, refresh);
    };
  }, [refresh]);

  const { visitorPortal, contractorPortal, maxSites } = entitlement;

  // The module needs BOTH the company entitlement and a personal invite right. An entitled
  // company whose employee has no rights should not see the nav item at all.
  const canSeeVisitors =
    visitorPortal && (permissions.canVisitor || permissions.canContractor);

  return {
    visitorPortal,
    contractorPortal,
    maxSites,
    canSeeVisitors,
    canInviteVisitor: visitorPortal && permissions.canVisitor,
    // Contractor invites need the paid add-on as well as the personal right.
    canInviteContractor: contractorPortal && permissions.canContractor,
    canAssignDesk: permissions.canDesk,
    canAssignParking: permissions.canParking,
    // The reception dashboard (RON-5469). Gated on EITHER portal rather than on `visitorPortal`
    // alone: a front desk signs in whoever turns up, and a company that bought only the
    // contractor add-on still has a desk with contractors arriving at it.
    //
    // Coerced to a real boolean, because the Sidebar's nav filter hides an item only when
    // `visible` is exactly `false` — an undefined capability would show a link to a screen the
    // server refuses.
    canReception: Boolean((visitorPortal || contractorPortal) && permissions.canReception),
  };
}

export default useVisitorAccess;
